import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import TenantSelector from './pages/TenantSelector';
import TenantDashboard from './pages/TenantDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import CallCenterWorkspace from './pages/CallCenterWorkspace';
import ConversationMonitor from './pages/ConversationMonitor';
import KnowledgeBase from './pages/KnowledgeBase';
import TeamManagement from './pages/TeamManagement';
import SallusDashboard from './pages/SallusDashboard';
import SallusMarketplace from './pages/SallusMarketplace';
import SallusAnalytics from './pages/SallusAnalytics';
import SallusSettings from './pages/SallusSettings';
import AddTenant from './pages/AddTenant';
import TenantUsers from './pages/TenantUsers';
import TenantAgents from './pages/TenantAgents';
import BillingPage from './pages/BillingPage';
import ReferralPage from './pages/ReferralPage';
import ResponseTemplatesPage from './pages/ResponseTemplatesPage';
import MarketingTemplatesPage from './pages/MarketingTemplatesPage';
import OptOutBlacklistPage from './pages/OptOutBlacklistPage';
import ProtectedRoute from './components/ProtectedRoute';
import RegisterPage from './pages/RegisterPage';
import OnboardingWizard from './pages/OnboardingWizard';
import TasksPage from './pages/TasksPage';
import AutomationsPage from './pages/AutomationsPage';
import CampaignsPage from './pages/CampaignsPage';
import AuditLogPage from './pages/AuditLogPage';
import AgentActivityPage from './pages/AgentActivityPage';
import TaskTemplatesAdmin from './pages/TaskTemplatesAdmin';
import HelpAdmin from './pages/HelpAdmin';
import HelpChat from './components/HelpChat';
import TermsPage from './pages/TermsPage';
import AlexaLinkPage from './pages/AlexaLinkPage';
import ConversationLogs from './pages/ConversationLogs';
import AuthCallback from './pages/AuthCallback';
import PublicAgentPage from './pages/PublicAgentPage';

function RedirectToAgentsConfig() {
  const { tenantId } = useParams();
  return <Navigate to={`/agents-config/${tenantId}`} replace />;
}

const API_URL = process.env.REACT_APP_API_URL;
const NO_SIDEBAR_PATHS = ['/login', '/register', '/welcome', '/terms', '/auth/', '/alexa-link', '/u/', '/unauthorized'];

function AppLayout({ children }) {
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const noSidebar = !user || NO_SIDEBAR_PATHS.some(p => location.pathname.startsWith(p));

  const match = location.pathname.match(/\/[^/]+\/([^/]+)/);
  const tenantId = match?.[1];
  const hasTenant = tenantId && !tenantId.startsWith('sallus') && location.pathname !== '/tenants' && !location.pathname.startsWith('/u/');

  // audience do tier — lê do sessionStorage primeiro para evitar flash B2B→B2C
  const cacheKey = tenantId ? `tierAudience_${tenantId}` : null;
  const cached = cacheKey ? sessionStorage.getItem(cacheKey) : null;
  const [audience, setAudience] = useState(cached); // síncrono se já tiver cache

  useEffect(() => {
    if (!hasTenant || !cacheKey || cached) return; // já tem cache, não precisa buscar
    Promise.all([
      fetch(`${API_URL}/public/tiers`).then(r => r.json()).catch(() => ({ tiers: [] })),
      import('./services/api').then(m => m.default.get(`/tenants/${tenantId}`)).catch(() => ({ data: {} }))
    ]).then(([tiersRes, tenantRes]) => {
      const tier = tenantRes.data?.tier;
      const found = (tiersRes.tiers || []).find(t => t.tierId === tier);
      const aud = found?.audience || 'business';
      sessionStorage.setItem(cacheKey, aud);
      setAudience(aud);
    });
  }, [tenantId]); // eslint-disable-line

  if (noSidebar || !hasTenant) return children;
  // Aguarda audience ser resolvido antes de montar a sidebar
  // Evita montar com isPersonal=false e depois tentar atualizar
  if (audience === null) return children;

  // B2C não exibe sidebar — dashboard próprio com navegação inline
  if (audience === 'personal') return children;

  return (
    <>
      <Sidebar
        tenantId={tenantId}
        role={user.role}
        isPersonal={false}
      />
      <MainContent>
        {children}
      </MainContent>
    </>
  );
}

const MainContent = styled.div`
  margin-left: var(--sidebar-width, 0px);
  transition: margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <Router>
          <AppLayout>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/welcome" element={<OnboardingWizard />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/alexa-link" element={<AlexaLinkPage />} />
        <Route path="/u/:userSlug/:agentSlug" element={<PublicAgentPage />} />
        
        <Route path="/" element={<Navigate to="/login" replace />} />
        
        <Route path="/tenants" element={
          <ProtectedRoute>
            <TenantSelector />
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
            <TenantDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/users/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
            <TenantUsers />
          </ProtectedRoute>
        } />
        
        <Route path="/agents-config/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
            <TenantAgents />
          </ProtectedRoute>
        } />
        
        <Route path="/agents/:tenantId/:agentId/config" element={<RedirectToAgentsConfig />} />
        
        <Route path="/manager/:tenantId" element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'tenant_admin']}>
            <ManagerDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/callcenter/:tenantId" element={
          <ProtectedRoute allowedRoles={['agent', 'admin', 'tenant_admin']}>
            <CallCenterWorkspace />
          </ProtectedRoute>
        } />
        
        <Route path="/monitor/:tenantId" element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'tenant_admin']}>
            <ConversationMonitor />
          </ProtectedRoute>
        } />
        
        <Route path="/knowledge/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
            <KnowledgeBase />
          </ProtectedRoute>
        } />
        
        <Route path="/billing/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
            <BillingPage />
          </ProtectedRoute>
        } />

        <Route path="/referral/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
            <ReferralPage />
          </ProtectedRoute>
        } />

        <Route path="/tasks/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin', 'manager']}>
            <TasksPage />
          </ProtectedRoute>
        } />

        <Route path="/automations/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
            <AutomationsPage />
          </ProtectedRoute>
        } />

        <Route path="/campaigns/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin', 'manager']}>
            <CampaignsPage />
          </ProtectedRoute>
        } />

        <Route path="/optout/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin', 'manager']}>
            <OptOutBlacklistPage />
          </ProtectedRoute>
        } />

        <Route path="/audit/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin', 'sallus_admin']}>
            <AuditLogPage />
          </ProtectedRoute>
        } />

        <Route path="/agents-activity/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin', 'manager']}>
            <AgentActivityPage />
          </ProtectedRoute>
        } />
        <Route path="/logs/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin', 'manager', 'sallus_admin']}>
            <ConversationLogs />
          </ProtectedRoute>
        } />

        <Route path="/templates/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin', 'manager']}>
            <ResponseTemplatesPage />
          </ProtectedRoute>
        } />

        <Route path="/mktpl/:tenantId" element={
          <ProtectedRoute allowedRoles={['admin', 'tenant_admin', 'manager']}>
            <MarketingTemplatesPage />
          </ProtectedRoute>
        } />
        
        <Route path="/team/:tenantId" element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'tenant_admin']}>
            <TeamManagement />
          </ProtectedRoute>
        } />
        
        <Route path="/sallus/dashboard" element={
          <ProtectedRoute allowedRoles={['sallus_admin']}>
            <SallusDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/sallus/marketplace" element={
          <ProtectedRoute allowedRoles={['sallus_admin']}>
            <SallusMarketplace />
          </ProtectedRoute>
        } />
        
        <Route path="/sallus/analytics" element={
          <ProtectedRoute allowedRoles={['sallus_admin']}>
            <SallusAnalytics />
          </ProtectedRoute>
        } />

        <Route path="/sallus/settings" element={
          <ProtectedRoute allowedRoles={['sallus_admin']}>
            <SallusSettings />
          </ProtectedRoute>
        } />

        <Route path="/sallus/task-templates" element={
          <ProtectedRoute allowedRoles={['sallus_admin']}>
            <TaskTemplatesAdmin />
          </ProtectedRoute>
        } />

        <Route path="/sallus/help" element={
          <ProtectedRoute allowedRoles={['sallus_admin']}>
            <HelpAdmin />
          </ProtectedRoute>
        } />
        
        <Route path="/sallus/tenants/new" element={
          <ProtectedRoute allowedRoles={['sallus_admin']}>
            <AddTenant />
          </ProtectedRoute>
        } />
        
        <Route path="/sallus/tenants/edit/:tenantId" element={
          <ProtectedRoute allowedRoles={['sallus_admin']}>
            <AddTenant editMode />
          </ProtectedRoute>
        } />
        
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      <HelpChat />
          </AppLayout>
        </Router>
      </ConfirmProvider>
    </ToastProvider>
  );
}


const Unauthorized = () => {
  const { t } = useTranslation();
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h1>❌ {t('accessDenied')}</h1>
      <p>{t('accessDeniedMsg')}</p>
      <button onClick={() => window.location.href = '/login'}>{t('backToLogin')}</button>
    </div>
  );
};

export default App;
